<div class="global-search-container">

  <!-- Backdrop scrim -->
  @if (showOverlay) {
    <div class="search-backdrop" [@backdropFade] (click)="onBackdropClick()"></div>
  }

  <!-- Always-visible search input -->
  <div class="search-input-wrapper" [class.focused]="showOverlay">
    <mat-icon class="search-icon">search</mat-icon>
    <input #searchInput
      [formControl]="searchControl"
      [placeholder]="'Search...  '"
      autocomplete="off"
      [attr.maxlength]="maxQueryLength"
      (focus)="onInputFocus()"
      (keydown)="onKeyDown($event)" />
    @if (searchControl.value) {
      <mat-icon class="clear-icon" (click)="clearSearch()">close</mat-icon>
    }
    @if (isLoading) {
      <mat-progress-spinner
        [diameter]="16"
        mode="indeterminate"
        class="search-spinner">
      </mat-progress-spinner>
    }
  </div>

  <!-- Results dropdown overlay -->
  @if (showOverlay) {
    <div class="search-results-overlay" [@overlaySlideDown] (scroll)="onOverlayScroll($event)">
      <!-- Ask Google / Ask ChatGPT — first thing in the dropdown (not shown under an error) -->
      @if (searchControl.value && !errorMessage) {
        <div class="ask-external-bar">
          <div class="ask-external" (click)="askGoogle()">
            <img class="external-icon" src="https://www.google.com/favicon.ico" alt="Google" width="14" height="14" />
            <span>Search Google for "<strong>{{ searchControl.value }}</strong>"</span>
            <mat-icon class="external-arrow">open_in_new</mat-icon>
          </div>
          <div class="ask-external" (click)="askChatGPT()">
            <img class="external-icon" src="https://cdn.oaistatic.com/assets/favicon-o20kmmos.svg" alt="ChatGPT" width="14" height="14" />
            <span>Ask ChatGPT "<strong>{{ searchControl.value }}</strong>"</span>
            <mat-icon class="external-arrow">open_in_new</mat-icon>
          </div>
        </div>
      }
      <!-- Loading state: request in flight, no results rendered yet -->
      @if (isLoading && !hasSearched) {
        <div class="empty-state loading-state">
          <mat-progress-spinner [diameter]="32" mode="indeterminate"></mat-progress-spinner>
        </div>
      }
      <!-- Idle state: overlay open, nothing typed yet, no recent searches to show -->
      @if (!searchControl.value && recentSearches.length === 0) {
        <div class="empty-state idle-state">
          <div class="empty-icon-wrapper">
            <mat-icon class="empty-icon">manage_search</mat-icon>
          </div>
          <span class="empty-title">Type something to search...</span>
          <span class="empty-subtitle">Search across Bids, Jobs, Customers, Vendors and more</span>
        </div>
      }
      <!-- Recent searches (when input empty, overlay open) -->
      @if (!searchControl.value && recentSearches.length > 0) {
        <div class="section-header">
          <span class="section-label">Recent Searches</span>
          <span class="section-action" (click)="clearRecentSearches(); $event.stopPropagation()">Clear</span>
        </div>
        @for (term of recentSearches; track term) {
          <div class="recent-item"
            (click)="applyRecentSearch(term)">
            <mat-icon class="recent-icon">history</mat-icon>
            <span class="recent-text">{{ term }}</span>
            <mat-icon class="recent-delete" (click)="removeRecentSearch(term, $event)">close</mat-icon>
          </div>
        }
      }
      <!-- Results summary + category filter chips -->
      @if (hasSearched && hasResults) {
        <div class="results-summary">
          <span class="results-count">{{ totalResultCount }} results</span>
        </div>
      }
      @if (hasSearched && hasResults && categoryFilters.length > 1) {
        <div class="category-filters">
          @for (cat of categoryFilters; track cat) {
            <button class="category-chip"
              [class.active]="cat.active" (click)="selectCategory(cat.key); $event.stopPropagation()"
              [matTooltip]="cat.key === 'all' ? 'Show all results' : 'Show only ' + cat.label + ' results'"
              matTooltipPosition="above">
              @if (cat.key !== 'all') {
                <mat-icon class="chip-icon">{{ cat.icon }}</mat-icon>
              }
              <span>{{ cat.label }}</span>
              <span class="chip-count">{{ cat.count }}</span>
            </button>
          }
        </div>
      }
      <!-- Grouped results -->
      @for (group of filteredResults; track group) {
        @if (group.items.length > 0) {
          <div class="result-group" [@staggerGroup]>
            <div class="section-header">
              <div class="module-icon-circle"
                [style.background]="globalSearchService.getModuleTheme(group.module)?.iconBg"
                [style.color]="globalSearchService.getModuleTheme(group.module)?.iconColor">
                <mat-icon class="section-icon">{{ group.moduleIcon }}</mat-icon>
              </div>
              <span class="section-label">{{ group.moduleLabel }}</span>
              <span class="section-badge">{{ group.total }}</span>
            </div>
            @for (item of group.items; track item; let i = $index) {
              <div class="result-item"
                [class.selected]="isItemSelected(group, i)"
                [class.no-route]="!item.route"
                [attr.title]="item.route ? null : 'The record this document belongs to cannot be opened from search'"
                [style.--accent-color]="globalSearchService.getModuleTheme(group.module)?.iconColor"
                (click)="navigateToItem(item)">
                <div class="result-main">
                  @if (item.module === 'document') {
                    <mat-icon class="doc-type-icon">{{ item.isFolder ? 'folder' : 'insert_drive_file' }}</mat-icon>
                  }
                  <div class="result-info">
                    @if (item.module === 'document') {
                      <!-- Documents keep their original, unchanged row: no genuine
                           identifier/description exists to merge in (see result-line1
                           below for every other module). -->
                      <span class="result-title" [innerHTML]="highlight(item.title)"></span>
                      @if (item.subtitle) {
                        <span class="result-subtitle" [innerHTML]="highlight(item.subtitle)"></span>
                      }
                    } @else {
                      <span class="result-line1">
                        @if (item.subtitle) {
                          <span class="result-identifier">{{ item.subtitle }}</span>
                        }
                        <span class="result-name" [innerHTML]="highlight(item.title)"></span>
                      </span>
                      @if (item.description) {
                        <span class="result-description" [innerHTML]="highlight(item.description)"></span>
                      }
                      <!-- Bid items — bid type chip + customer + due date; Job
                           items — start date; Proposal items — scope chip +
                           customer, same meta row. Same "Bid & Customer"
                           pairing, 'Customer: X' tooltip convention, and
                           tag-badge/'Bid type: X' tooltip convention as the
                           Bid Room dashboard widget (bid-room-widget
                           .component.html's bid.owner meta-item and bid.tags
                           chip-badges) — type chip first, customer after, due
                           date last, on one row. bidTypeName/dueDate are only
                           ever populated on Bid items, startDate only ever on
                           Job items, and scopeName only ever on Proposal items
                           (see IndexSearchDocument::buildBid()/buildJob()/
                           buildProposal()), so this row is naturally empty for
                           every other module without an extra per-module
                           check here. customerName is populated on both Bid
                           and Proposal items. -->
                      @if (item.customerName || item.bidTypeName || item.dueDate || item.startDate || item.scopeName) {
                        <span class="result-meta-row">
                          @if (item.bidTypeName) {
                            <span class="result-type-chip" [matTooltip]="'Bid type: ' + item.bidTypeName">{{ item.bidTypeName }}</span>
                          }
                          <!-- Proposal items only — its own active scope(s),
                               same gray-pill treatment as bidTypeName's chip
                               above (the two never both populate on the same
                               item, since they're module-exclusive). -->
                          @if (item.scopeName) {
                            <span class="result-type-chip" [matTooltip]="'Scope: ' + item.scopeName">{{ item.scopeName }}</span>
                          }
                          @if (item.customerName) {
                            <span class="result-customer" [matTooltip]="'Customer: ' + item.customerName">
                              <mat-icon class="result-customer-icon">business</mat-icon>
                              <span class="result-customer-name">{{ item.customerName }}</span>
                            </span>
                          }
                          @if (item.dueDate) {
                            <span class="result-due-date" [matTooltip]="'Due: ' + (item.dueDate | date: 'MM/dd/yyyy')">
                              <mat-icon class="result-due-icon">event</mat-icon>
                              {{ item.dueDate | date: 'MM/dd/yyyy' }}
                            </span>
                          }
                          <!-- Job items only — start_date. Was icon-only (per
                               the original request); per the follow-up, now
                               shows the date text too, same icon+text+tooltip
                               treatment as Bid's due date right above it. -->
                          @if (item.startDate) {
                            <span class="result-start-date" [matTooltip]="'Start date: ' + (item.startDate | date: 'MM/dd/yyyy')">
                              <mat-icon class="result-due-icon">event</mat-icon>
                              {{ item.startDate | date: 'MM/dd/yyyy' }}
                            </span>
                          }
                        </span>
                      }
                    }
                  </div>
                </div>
                <!-- The record's real team — e.g. a Bid's Sales Rep + Estimator +
                     Project Manager + Assigned To — absent (not a placeholder) when the
                     record has no role filled in, or for a module with no team/assignee
                     concept. Never permission-gated (not financial data) — see
                     GlobalSearchApiHit.team/assignee_id/assignee_name. Positioned before
                     the amount, matching the small stacked initials-avatar convention
                     used on the Bids/Jobs list "Team" column (app-avatar-group) — a
                     single member looks identical to the previous single-avatar +
                     name-text treatment for every module except Customer and Job
                     (per the request, circle-only there, name moved into the
                     tooltip); more than one stacks the circles instead (already
                     circle-only). -->
                @if (teamAvatars(item); as avatars) {
                  @if (avatars.length) {
                    <span class="result-assignee-group">
                      @if (avatars.length === 1) {
                        <span class="result-assignee" [class.has-photo]="avatars[0].profileImageUrl" [style.background-color]="avatars[0].profileImageUrl ? null : avatars[0].color" [matTooltip]="avatars[0].tooltip" matTooltipPosition="above">
                          <!-- Same real uploaded photo app-avatar-group already shows
                               for this exact person elsewhere (Bid/Job detail header)
                               — falls back to initials when they have none uploaded. -->
                          @if (avatars[0].profileImageUrl) {
                            <img [src]="avatars[0].profileImageUrl" alt="" class="result-assignee-photo" />
                          } @else {
                            {{ avatars[0].initials }}
                          }
                          <!-- Same small presence-status badge the real Bids/Jobs
                               list "Team" column avatars show (app-avatar-group) —
                               see GlobalSearchService.getTeamAvatars()'s statusSvg. -->
                          <span class="result-status-badge" [innerHTML]="avatars[0].statusSvg"></span>
                        </span>
                        @if (item.module !== 'customer' && item.module !== 'job') {
                          <span class="result-assignee-name">{{ avatars[0].name }}</span>
                        }
                      } @else {
                        <span class="result-team-stack">
                          @for (member of avatars; track member.name + member.roles.join(',')) {
                            <span class="result-assignee result-team-avatar" [class.has-photo]="member.profileImageUrl" [style.background-color]="member.profileImageUrl ? null : member.color" [matTooltip]="member.tooltip" matTooltipPosition="above">
                              @if (member.profileImageUrl) {
                                <img [src]="member.profileImageUrl" alt="" class="result-assignee-photo" />
                              } @else {
                                {{ member.initials }}
                              }
                              <span class="result-status-badge" [innerHTML]="member.statusSvg"></span>
                            </span>
                          }
                        </span>
                      }
                    </span>
                  }
                }
                <!-- Bid/Proposal/Job's genuine total value — absent (not a placeholder)
                     when the backend redacted it for a user without that module's View
                     Financial permission. See GlobalSearchApiHit.amount. -->
                @if (item.amount != null) {
                  <span class="result-amount">{{ item.amount | currency }}</span>
                }
                @if (item.route) {
                  <mat-icon class="result-arrow">arrow_forward</mat-icon>
                }
              </div>
            }
            @if (group.hasMore && activeFilter === 'all') {
              <div class="view-all-link"
                (click)="viewAllResults(group.module)">
                View all {{ group.total }} results
                <mat-icon>arrow_outward</mat-icon>
              </div>
            }
          </div>
        }
      }
      @if (isLoadingMore) {
        <div class="load-more-indicator">
          <mat-progress-spinner [diameter]="18" mode="indeterminate"></mat-progress-spinner>
        </div>
      }
      <!-- Error state (Elasticsearch unavailable / validation / network) — never shown as "no results" -->
      @if (errorMessage) {
        <div class="empty-state search-error-state">
          <div class="empty-icon-wrapper">
            <mat-icon class="empty-icon">error_outline</mat-icon>
          </div>
          <span class="empty-title">Search is unavailable</span>
          <span class="empty-subtitle">{{ errorMessage }}</span>
        </div>
      }
      <!-- Empty state when searched but no results -->
      @if (hasSearched && !hasResults && !errorMessage) {
        <div class="empty-state">
          <div class="empty-icon-wrapper">
            <mat-icon class="empty-icon">search_off</mat-icon>
          </div>
          <span class="empty-title">No results found</span>
          <span class="empty-subtitle">Try different keywords or check your spelling</span>
          <span class="empty-query">Searched for: <strong>"{{ searchControl.value }}"</strong></span>
        </div>
      }
    </div>
  }
</div>
